import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface HistorySession {
  id: string
  userId: string
  cigarId: string
  smokedDate: string
  rating?: number
  startTime?: string
  endTime?: string
  durationMinutes?: number
  sessionRating?: {
    overall: number
  }
  tastingNotes?: string
  location?: string
  setting?: string
  createdAt: string
  cigar: {
    id: string
    vitola: { id: string; name: string }
    cigarLine: {
      id: string
      name: string
      brand: {
        id: string
        name: string
      }
    }
  }
}

export interface HistoryPagination {
  page: number
  limit: number
  totalCount: number
  totalPages: number
}

export interface HistoryResponse {
  sessions: HistorySession[]
  pagination: HistoryPagination
}

export interface TopBrand {
  id: string
  name: string
  count: number
}

export interface HistorySummary {
  totalSmoked: number
  averageRating: number | null
  topBrands: TopBrand[]
}

export interface HistoryParams {
  page?: number
  brandId?: string
  dateFrom?: string
  dateTo?: string
  rating?: string
  search?: string
}

export const historyApi = createApi({
  reducerPath: 'historyApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/history',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['History'],
  endpoints: (builder) => ({
    getHistory: builder.query<HistoryResponse, HistoryParams>({
      query: (params) => ({ url: '', params }),
      transformResponse: (response: { data: { sessions: HistorySession[]; pagination: HistoryPagination } }) => ({
        sessions: response.data.sessions,
        pagination: response.data.pagination,
      }),
      providesTags: ['History'],
    }),

    getHistorySummary: builder.query<HistorySummary, void>({
      query: () => '/summary',
      transformResponse: (response: { data: { summary: HistorySummary } }) => response.data.summary,
      providesTags: ['History'],
    }),

    getCigarHistoryCount: builder.query<number, string>({
      query: (cigarId) => `/cigar/${cigarId}/count`,
      transformResponse: (response: { data: { count: number } }) => response.data.count,
      providesTags: (_result, _error, cigarId) => [{ type: 'History', id: `cigar-${cigarId}` }],
    }),
  }),
})

export const {
  useGetHistoryQuery,
  useGetHistorySummaryQuery,
  useGetCigarHistoryCountQuery,
} = historyApi
